import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
  Brain,
  CircleNotch,
  GitBranch,
  PushPin,
  PushPinSlash,
  TrashSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@alfred/convex/_generated/api";
import type { Doc, Id } from "@alfred/convex/_generated/dataModel";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { cn, relativeTime } from "~/lib/utils";

export const Route = createFileRoute("/memory")({
  component: MemoryPage,
});

const KIND_META: Record<
  string,
  { label: string; accent: string }
> = {
  structure: { label: "Structure", accent: "bg-[color:var(--status-info)]" },
  convention: { label: "Convention", accent: "bg-[color:var(--brand-purple)]" },
  commands: { label: "Commands", accent: "bg-[color:var(--brand-orange)]" },
  "key-file": { label: "Key file", accent: "bg-[color:var(--brand-blue)]" },
  pitfall: { label: "Pitfall", accent: "bg-[color:var(--status-error)]" },
  pattern: { label: "Pattern", accent: "bg-[color:var(--status-success)]" },
  other: { label: "Note", accent: "bg-muted-foreground" },
};

function MemoryPage() {
  const all = useQuery(api.memories.list, {});
  const counts = useQuery(api.memories.counts, {});
  const [activeRepo, setActiveRepo] = React.useState<string | null>(null);

  const grouped = React.useMemo(() => {
    const map = new Map<string, Doc<"memories">[]>();
    for (const m of all ?? []) {
      if (!map.has(m.repoFullName)) map.set(m.repoFullName, []);
      map.get(m.repoFullName)!.push(m);
    }
    return [...map.entries()].sort((a, b) =>
      a[0].localeCompare(b[0]),
    );
  }, [all]);

  React.useEffect(() => {
    if (!activeRepo && grouped[0]) setActiveRepo(grouped[0][0]);
  }, [activeRepo, grouped]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b border-border/70 bg-background/85 px-4 py-4 backdrop-blur lg:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Brain size={18} weight="duotone" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">
              Repo Memory
            </h1>
            <p className="text-xs text-muted-foreground">
              Durable facts Alfred has learned about each repo. Loaded into
              every prompt — and into <code className="rounded bg-muted px-1 py-0.5">.alfred/MEMORY.md</code> — so the agent doesn't re-discover the
              codebase each run.
            </p>
          </div>
          <div className="ml-auto text-xs text-muted-foreground">
            {counts ? (
              <>
                <span className="font-semibold text-foreground">{counts.total}</span> entries across{" "}
                <span className="font-semibold text-foreground">
                  {Object.keys(counts.byRepo).length}
                </span>{" "}
                repo(s)
              </>
            ) : (
              "Loading…"
            )}
          </div>
        </div>
      </header>

      {all === undefined ? (
        <div className="grid h-full place-items-center text-sm text-muted-foreground">
          <CircleNotch size={18} className="animate-spin" />
        </div>
      ) : grouped.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[18rem_1fr]">
          {/* Repo list */}
          <aside className="border-r border-border/60 bg-muted/30 overflow-y-auto p-3">
            <ul className="space-y-1">
              {grouped.map(([repo, entries]) => (
                <li key={repo}>
                  <button
                    type="button"
                    onClick={() => setActiveRepo(repo)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-colors",
                      activeRepo === repo
                        ? "border-primary/60 bg-primary/10 text-foreground"
                        : "border-transparent hover:bg-card hover:border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <GitBranch
                      size={14}
                      weight="bold"
                      className="shrink-0"
                    />
                    <span className="min-w-0 flex-1 truncate font-mono text-xs">
                      {repo}
                    </span>
                    <Badge variant="muted">{entries.length}</Badge>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Entries for active repo */}
          <main className="min-h-0 overflow-y-auto px-4 py-4 lg:px-6">
            {activeRepo ? (
              <RepoMemory
                repoFullName={activeRepo}
                entries={
                  grouped.find(([r]) => r === activeRepo)?.[1] ?? []
                }
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Pick a repo on the left.
              </p>
            )}
          </main>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="grid h-full place-items-center px-6 text-center">
      <div className="max-w-md">
        <Brain
          size={48}
          weight="duotone"
          className="mx-auto mb-3 text-muted-foreground"
        />
        <h2 className="text-base font-semibold">No memories yet</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Alfred builds memory after every successful run. The distiller
          extracts durable facts about each repository — structure, naming
          conventions, useful commands, gotchas — and stores them here so the
          next run starts smarter.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Kick off a ticket from the{" "}
          <Link to="/" className="text-primary hover:underline">
            board
          </Link>{" "}
          and come back when it merges.
        </p>
      </div>
    </div>
  );
}

function RepoMemory({
  repoFullName,
  entries,
}: {
  repoFullName: string;
  entries: Doc<"memories">[];
}) {
  const update = useMutation(api.memories.update);
  const remove = useMutation(api.memories.remove);

  const byKind = React.useMemo(() => {
    const map = new Map<string, Doc<"memories">[]>();
    for (const e of entries) {
      const k = e.kind in KIND_META ? e.kind : "other";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(e);
    }
    return [...map.entries()].sort((a, b) => {
      const order = Object.keys(KIND_META);
      return order.indexOf(a[0]) - order.indexOf(b[0]);
    });
  }, [entries]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-sm">{repoFullName}</h2>
        <span className="text-xs text-muted-foreground">
          {entries.length} entr{entries.length === 1 ? "y" : "ies"}
        </span>
      </div>
      {byKind.map(([kind, items]) => (
        <Card key={kind} className="overflow-hidden">
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 border-b border-border/60 pb-2">
            <span
              className={cn(
                "h-2 w-2 rounded-full",
                KIND_META[kind]?.accent ?? "bg-muted-foreground",
              )}
            />
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
              {KIND_META[kind]?.label ?? kind}
            </CardTitle>
            <span className="ml-auto text-[10px] uppercase text-muted-foreground">
              {items.length}
            </span>
          </CardHeader>
          <CardContent className="space-y-2 p-3">
            {items.map((e) => (
              <MemoryRow
                key={e._id}
                entry={e}
                onTogglePin={() =>
                  update({ id: e._id, pinned: !e.pinned })
                    .then(() =>
                      toast.success(e.pinned ? "Unpinned" : "Pinned"),
                    )
                    .catch((err) => toast.error(err.message))
                }
                onDelete={() =>
                  remove({ id: e._id })
                    .then(() => toast.success("Deleted"))
                    .catch((err) => toast.error(err.message))
                }
                onEditContent={(content) =>
                  update({ id: e._id, content })
                    .then(() => toast.success("Saved"))
                    .catch((err) => toast.error(err.message))
                }
              />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function MemoryRow({
  entry,
  onTogglePin,
  onDelete,
  onEditContent,
}: {
  entry: Doc<"memories">;
  onTogglePin: () => void;
  onDelete: () => void;
  onEditContent: (content: string) => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(entry.content);
  React.useEffect(() => setDraft(entry.content), [entry.content]);

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-card p-3 transition-colors",
        entry.pinned && "border-primary/40 bg-primary/[0.04]",
      )}
    >
      <div className="flex items-center gap-2 text-xs">
        <span className="font-mono font-semibold text-foreground">
          {entry.topic}
        </span>
        {entry.pinned ? (
          <Badge variant="default">
            <PushPin size={10} weight="fill" /> pinned
          </Badge>
        ) : null}
        <span className="ml-auto text-[10px] text-muted-foreground">
          {entry.sourcePrReview
            ? `from PR review · `
            : entry.sourceJiraKey
              ? `from ${entry.sourceJiraKey} · `
              : ""}
          updated {relativeTime(entry.updatedAt)}
        </span>
        <button
          type="button"
          onClick={onTogglePin}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          title={entry.pinned ? "Unpin" : "Pin"}
        >
          {entry.pinned ? (
            <PushPinSlash size={12} weight="bold" />
          ) : (
            <PushPin size={12} weight="bold" />
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm(`Delete memory "${entry.topic}"?`)) onDelete();
          }}
          className="rounded-md p-1 text-muted-foreground hover:bg-[color:var(--status-error)]/15 hover:text-[color:var(--status-error)]"
          title="Delete"
        >
          <TrashSimple size={12} weight="bold" />
        </button>
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="block w-full resize-y rounded-lg border border-border bg-background p-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setDraft(entry.content);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                onEditContent(draft);
                setEditing(false);
              }}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p
          className="mt-1 text-sm leading-relaxed text-foreground/90"
          onDoubleClick={() => setEditing(true)}
          title="Double-click to edit"
        >
          {entry.content}
        </p>
      )}
    </div>
  );
}
